import { ContentV1GetPresetDataSourcesResponse } from '@antimatterhq/asl-api-ts-v1';
import { useNavigate } from 'react-router-dom';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@antimatterhq/ui/components/card';
import { datadogLogs } from '@datadog/browser-logs';
import Tooltip from '@antimatterhq/ui/components/tooltip';
import { DATADOG_MESSAGE, getDatadogPreset } from '@/utils/datadogUtils';
import { Medallion } from '@/components/medallion/Medallion';

type PresetDataSource = NonNullable<
  ContentV1GetPresetDataSourcesResponse['items']
>[number];

interface DatasourcePresetCardProps {
  preset: PresetDataSource;
  'data-testid'?: string;
}

export const DatasourcePresetCard = (props: DatasourcePresetCardProps) => {
  const { preset, 'data-testid': testId } = props;
  const { title, description, author } = preset;
  const navigate = useNavigate();

  const handleAddDataSource = (e: React.MouseEvent) => {
    e.stopPropagation();
    const presetSource = preset;
    datadogLogs.logger.info(
      DATADOG_MESSAGE.DATASOURCE.ADD,
      getDatadogPreset(presetSource),
    );
    navigate(
      `/configure/data-sources/add/getting-started?source=${presetSource.source}&sourceType=${presetSource.sourceType}&usePreset=${presetSource.name}`,
    );
  };

  return (
    <>
      <Card
        className={'flex min-h-60 w-full flex-col px-4 py-4'}
        data-testid={testId}
      >
        <CardHeader className="space-y-1 p-0">
          <div className="flex justify-between gap-2">
            <div className="flex-1">
              <Tooltip side="top" content={title}>
                <Text
                  size="lg"
                  semiBold
                  className="line-clamp-1 overflow-hidden text-ellipsis"
                  data-testid={`${testId}-title`}
                >
                  {title}
                </Text>
              </Tooltip>
              <Text
                size="sm"
                color="secondary"
                className="line-clamp-3 overflow-hidden text-ellipsis"
                data-testid={`${testId}-description`}
              >
                {description}
                {description && 'author' in preset && author && <br />}
                {'author' in preset && author && `By ${author}`}
              </Text>
            </div>
            <div>
              {preset.targetMedallion && (
                <Medallion
                  medallion={
                    preset.targetMedallion as 'bronze' | 'silver' | 'gold'
                  }
                />
              )}
            </div>
          </div>
        </CardHeader>

        <Spacer size="base" />

        <CardContent className="mt-auto flex w-full items-center justify-center p-0">
          <img
            className="h-16 w-40 overflow-hidden rounded-md object-contain"
            src={preset.iconURL ?? '/images/default_icon.png'}
            alt={preset.name ?? ''}
            data-testid={`${testId}-icon`}
          />
        </CardContent>

        <Spacer size="base" />

        <CardFooter className="mt-auto px-0 py-0">
          <Button
            variant="default"
            className="w-full"
            onClick={handleAddDataSource}
            data-testid={`${testId}-add-button`}
          >
            Add
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};

export default DatasourcePresetCard;
